import {
  Brain,
  Target,
  CheckCircle,
  Star,
  BookOpen,
  Users,
  RefreshCw,
  Lightbulb,
  Eye,
  Headphones,
  Zap,
  BarChart3,
  TrendingUp,
  Award,
} from "lucide-react";
import { tC } from "../../utils/TranslateWithContentLanguage";
import CardImage from "../tool_components/CardImage";
import { Button } from "@core/index";
import ChoiceBoardCardPDF from "./ChoiceBoardCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { useState } from "react";
import { useAppSelector } from "@packages/store/hooks";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetChoiceBoardQueryKey,
  getGetUserChoiceBoardsQueryKey,
  updateChoiceBoard,
} from "@packages/api/madlenAPI";
import {
  ChoiceBoardResponseDto,
  ActivityItem,
  ChoiceBoardUpdateRequestDto,
} from "@packages/api/madlenAPI.schemas";

interface ChoiceBoardCardProps {
  choiceBoard: ChoiceBoardResponseDto;
  isEditable?: boolean;
  onCreateAgain?: () => void;
}

const DifficultyDistributionChart = ({
  distributionString,
  contentLanguage,
}: {
  distributionString: string;
  contentLanguage: string;
}) => {
  let distribution: Record<string, number> = {};
  try {
    distribution = JSON.parse(distributionString);
  } catch (error) {
    distribution = { easy: 33, medium: 33, hard: 34 };
  }

  const total = Object.values(distribution).reduce(
    (sum, value) => sum + value,
    0,
  );

  const difficultyConfig = {
    easy: {
      color: "bg-green-500",
      lightColor: "bg-green-100 dark:bg-green-900/30",
      textColor: "text-green-700 dark:text-green-300",
      icon: "🟢",
    },
    medium: {
      color: "bg-yellow-500",
      lightColor: "bg-yellow-100 dark:bg-yellow-900/30",
      textColor: "text-yellow-700 dark:text-yellow-300",
      icon: "🟡",
    },
    hard: {
      color: "bg-red-500",
      lightColor: "bg-red-100 dark:bg-red-900/30",
      textColor: "text-red-700 dark:text-red-300",
      icon: "🔴",
    },
  };

  return (
    <div className="space-y-4">
      {/* Progress Bars */}
      <div className="space-y-3">
        {Object.entries(distribution).map(([level, value]) => {
          const percentage = total > 0 ? (value / total) * 100 : 0;
          const config =
            difficultyConfig[
              level.toLowerCase() as keyof typeof difficultyConfig
            ] || difficultyConfig.medium;

          return (
            <div key={level} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{config.icon}</span>
                  <span className="font-medium text-gray-700 dark:text-gray-300 capitalize">
                    {tC(
                      contentLanguage,
                      `choiceBoards.difficulty.${level.toLowerCase()}`,
                    )}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-bold ${config.textColor}`}>
                    {percentage.toFixed(0)}%
                  </span>
                </div>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className={`${config.color} h-full transition-all duration-500 ease-out rounded-full`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ChoiceBoardCard = ({
  choiceBoard,
  onCreateAgain,
  isEditable = false,
}: ChoiceBoardCardProps) => {
  const finalImageUrl = useImagePdf(choiceBoard.imageUrl);
  const contentLanguage = choiceBoard.contentLanguage || "tr";
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  // Helper function to update choice board
  const updateChoiceBoardData = async (
    updateData: ChoiceBoardUpdateRequestDto,
  ) => {
    await updateChoiceBoard(choiceBoard.id!, updateData);

    await queryClient.invalidateQueries({
      queryKey: getGetUserChoiceBoardsQueryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: getGetChoiceBoardQueryKey(choiceBoard.id!),
    });
  };

  // Helper function to update activities
  const updateActivity = async (
    activityIndex: number,
    field: keyof ActivityItem,
    newValue: string,
  ) => {
    if (!choiceBoard.activities) return;

    const updatedActivities = [...choiceBoard.activities];
    updatedActivities[activityIndex] = {
      ...updatedActivities[activityIndex],
      [field]: newValue,
    };

    const updateData: ChoiceBoardUpdateRequestDto = {
      activities: updatedActivities,
    };

    await updateChoiceBoardData(updateData);
  };

  const getDifficultyConfig = (level?: string) => {
    const normalizedLevel = level?.toLowerCase() || "";
    if (
      normalizedLevel.includes("easy") ||
      normalizedLevel.includes("beginner")
    ) {
      return {
        color: "border-green-200 dark:border-green-800",
        bgColor:
          "bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10",
        badge:
          "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
        accent: "bg-green-500",
      };
    }
    if (
      normalizedLevel.includes("medium") ||
      normalizedLevel.includes("intermediate")
    ) {
      return {
        color: "border-yellow-200 dark:border-yellow-800",
        bgColor:
          "bg-gradient-to-br from-yellow-50 to-yellow-100/50 dark:from-yellow-900/20 dark:to-yellow-800/10",
        badge:
          "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300",
        accent: "bg-yellow-500",
      };
    }
    if (
      normalizedLevel.includes("hard") ||
      normalizedLevel.includes("advanced")
    ) {
      return {
        color: "border-red-200 dark:border-red-800",
        bgColor:
          "bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10",
        badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
        accent: "bg-red-500",
      };
    }
    return {
      color: "border-blue-200 dark:border-blue-800",
      bgColor:
        "bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10",
      badge: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      accent: "bg-blue-500",
    };
  };

  const getLearningStyleIcon = (style?: string) => {
    if (!style) return <Brain size={16} />;
    const normalizedStyle = style.toLowerCase();
    if (normalizedStyle.includes("visual")) return <Eye size={16} />;
    if (normalizedStyle.includes("auditory")) return <Headphones size={16} />;
    if (normalizedStyle.includes("kinesthetic")) return <Zap size={16} />;
    if (normalizedStyle.includes("social")) return <Users size={16} />;
    if (normalizedStyle.includes("analytical")) return <BarChart3 size={16} />;
    if (
      normalizedStyle.includes("reading") ||
      normalizedStyle.includes("writing")
    )
      return <BookOpen size={16} />;
    return <Brain size={16} />;
  };

  return (
    <div
      data-testid="choiceboard-card"
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20"
    >
      <div className="container max-w-7xl mx-auto px-4 py-4">
        {choiceBoard.imageUrl && (
          <CardImage
            createdAt={choiceBoard.createdAt}
            imageUrl={choiceBoard.imageUrl}
            videoUrl={choiceBoard.videoUrl}
            courseName={choiceBoard.courseName}
            gradeLevel={choiceBoard.gradeLevel}
            topic={choiceBoard.title || choiceBoard.prompt}
            theme="blue"
            contentLanguage={contentLanguage}
          />
        )}
        <div className="flex justify-end mb-6 gap-3 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 dark:hover:from-blue-700 dark:hover:to-blue-800"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            document={
              <ChoiceBoardCardPDF
                customizationSettings={customizationSettings}
                choiceBoard={{ ...choiceBoard, imageUrl: finalImageUrl }}
              />
            }
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            btnTheme="bg-gradient-to-r from-purple-500 to-purple-600"
            pdfTitle={choiceBoard.title || choiceBoard.prompt}
          />
        </div>

        <div className="space-y-8">
          {/* Choice Board Instructions */}
          {choiceBoard.choiceBoardInstructions && (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/50 dark:to-blue-800/30 text-blue-600 dark:text-blue-400">
                  <Lightbulb size={28} />
                </div>
                <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {tC(contentLanguage, "choiceBoards.instructions")}
                </h3>
              </div>
              <div className="prose prose-lg max-w-none dark:prose-invert">
                <EditableMarkdown
                  value={choiceBoard.choiceBoardInstructions}
                  isEditable={isEditable ?? true}
                  handleSave={async (newValue) => {
                    const updateData: ChoiceBoardUpdateRequestDto = {
                      choiceBoardInstructions: newValue,
                    };
                    await updateChoiceBoardData(updateData);
                  }}
                />
              </div>
            </div>
          )}

          {/* Advanced Settings Summary */}
          {(choiceBoard.preferredLearningStyles?.length ||
            choiceBoard.difficultyDistribution ||
            choiceBoard.selectionRequirements ||
            choiceBoard.learningOutcomesFocus?.length) && (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-3xl shadow-xl p-6 border border-gray-200/50 dark:border-gray-700/50">
              <div className="flex items-center gap-4 mb-8">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/50 dark:to-purple-800/30 text-purple-600 dark:text-purple-400">
                  <BarChart3 size={28} />
                </div>
                <h3 className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {tC(contentLanguage, "choiceBoards.advancedSettings")}
                </h3>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Difficulty Distribution - Enhanced */}
                {choiceBoard.difficultyDistribution && (
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-3 mb-6">
                      <TrendingUp
                        size={20}
                        className="text-indigo-600 dark:text-indigo-400"
                      />
                      <h4 className="text-xl font-bold text-indigo-600 dark:text-indigo-400">
                        {tC(
                          contentLanguage,
                          "choiceBoards.difficultyDistribution",
                        )}
                      </h4>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6 rounded-2xl border border-indigo-200/50 dark:border-indigo-800/50">
                      <DifficultyDistributionChart
                        distributionString={choiceBoard.difficultyDistribution}
                        contentLanguage={contentLanguage}
                      />
                    </div>
                  </div>
                )}

                {/* Learning Styles */}
                {choiceBoard.preferredLearningStyles &&
                  choiceBoard.preferredLearningStyles.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <Brain
                          size={20}
                          className="text-green-600 dark:text-green-400"
                        />
                        <h4 className="text-lg font-bold text-green-600 dark:text-green-400">
                          {tC(
                            contentLanguage,
                            "choiceBoards.assessmentCriteria",
                          )}
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {choiceBoard.preferredLearningStyles.map(
                          (style, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200/50 dark:border-green-800/50"
                            >
                              <span className="font-medium text-green-700 dark:text-green-300">
                                {style}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* Learning Outcomes Focus */}
                {choiceBoard.learningOutcomesFocus &&
                  choiceBoard.learningOutcomesFocus.length > 0 && (
                    <div>
                      <div className="flex items-center gap-3 mb-4">
                        <Target
                          size={20}
                          className="text-orange-600 dark:text-orange-400"
                        />
                        <h4 className="text-lg font-bold text-orange-600 dark:text-orange-400">
                          {tC(contentLanguage, "choiceBoards.studentResults")}
                        </h4>
                      </div>
                      <div className="space-y-2">
                        {choiceBoard.learningOutcomesFocus.map(
                          (outcome, index) => (
                            <div
                              key={index}
                              className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200/50 dark:border-orange-800/50"
                            >
                              <span className="font-medium text-orange-700 dark:text-orange-300">
                                {outcome}
                              </span>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                {/* Selection Requirements */}
                {choiceBoard.selectionRequirements && (
                  <div className="lg:col-span-2">
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle
                        size={20}
                        className="text-blue-600 dark:text-blue-400"
                      />
                      <h4 className="text-lg font-bold text-blue-600 dark:text-blue-400">
                        {tC(contentLanguage, "choiceBoards.assessmentCriteria")}
                      </h4>
                    </div>
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200/50 dark:border-blue-800/50">
                      <p className="text-blue-700 dark:text-blue-300 font-medium">
                        {choiceBoard.selectionRequirements}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Activities Grid */}
          {choiceBoard.activities && choiceBoard.activities.length > 0 && (
            <div>
              <div className="flex items-center gap-4 mb-8">
                <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/50 dark:to-emerald-800/30 text-emerald-600 dark:text-emerald-400">
                  <Star size={28} />
                </div>
                <h3 className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {tC(contentLanguage, "choiceBoards.activities")}
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {choiceBoard.activities.map(
                  (activity: ActivityItem, index: number) => {
                    const difficultyConfig = getDifficultyConfig(
                      activity.difficultyLevel,
                    );

                    return (
                      <div
                        key={index}
                        className={`
                          ${difficultyConfig.bgColor} 
                          ${difficultyConfig.color}
                          backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl 
                          transition-all duration-300 transform hover:-translate-y-1 
                          border-2 overflow-hidden group
                        `}
                      >
                        {/* Accent Bar */}
                        <div
                          className={`${difficultyConfig.accent} h-1 w-full`}
                        />

                        <div className="p-6">
                          {/* Activity Header */}
                          <div className="flex items-start justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200 flex-1 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                              <EditableMarkdown
                                value={
                                  activity.title ||
                                  `${tC(contentLanguage, "choiceBoards.activity")} ${index + 1}`
                                }
                                isEditable={isEditable ?? true}
                                handleSave={async (newValue) => {
                                  await updateActivity(
                                    index,
                                    "title",
                                    newValue,
                                  );
                                }}
                              />
                            </h3>
                            {/* Difficulty Badge */}
                            {activity.difficultyLevel && (
                              <span
                                className={`px-3 py-1 text-xs font-semibold rounded-full ${difficultyConfig.badge}`}
                              >
                                {activity.difficultyLevel}
                              </span>
                            )}
                          </div>

                          {/* Activity Description */}
                          {activity.detail && (
                            <div className="mb-4">
                              <div className="prose prose-sm max-w-none dark:prose-invert text-gray-700 dark:text-gray-300">
                                <EditableMarkdown
                                  value={activity.detail}
                                  isEditable={isEditable ?? true}
                                  handleSave={async (newValue) => {
                                    await updateActivity(
                                      index,
                                      "detail",
                                      newValue,
                                    );
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Learning Style */}
                          {activity.learningStyle && (
                            <div className="mb-4 flex items-center gap-2 text-sm">
                              <div className="p-2 rounded-lg bg-white/50 dark:bg-gray-800/50">
                                {getLearningStyleIcon(activity.learningStyle)}
                              </div>
                              <div>
                                <span className="font-medium text-gray-600 dark:text-gray-400">
                                  {tC(
                                    contentLanguage,
                                    "choiceBoards.learningStyle",
                                  )}
                                  :
                                </span>
                                <span className="ml-1 text-gray-800 dark:text-gray-200 font-medium">
                                  <EditableMarkdown
                                    value={activity.learningStyle}
                                    isEditable={isEditable ?? true}
                                    handleSave={async (newValue) => {
                                      await updateActivity(
                                        index,
                                        "learningStyle",
                                        newValue,
                                      );
                                    }}
                                  />
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Learning Outcomes */}
                          {activity.learningOutcomes && (
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Target
                                  size={14}
                                  className="text-purple-600 dark:text-purple-400"
                                />
                                <h4 className="font-semibold text-sm text-purple-600 dark:text-purple-400">
                                  {tC(
                                    contentLanguage,
                                    "choiceBoards.studentResults",
                                  )}
                                </h4>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 bg-white/30 dark:bg-gray-800/30 p-3 rounded-lg">
                                <div className="prose prose-sm max-w-none dark:prose-invert">
                                  <EditableMarkdown
                                    value={activity.learningOutcomes}
                                    isEditable={isEditable ?? true}
                                    handleSave={async (newValue) => {
                                      await updateActivity(
                                        index,
                                        "learningOutcomes",
                                        newValue,
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Skills Developed */}
                          {activity.skillsDeveloped && (
                            <div className="mb-4">
                              <div className="flex items-center gap-2 mb-2">
                                <Award
                                  size={14}
                                  className="text-orange-600 dark:text-orange-400"
                                />
                                <h4 className="font-semibold text-sm text-orange-600 dark:text-orange-400">
                                  {tC(
                                    contentLanguage,
                                    "choiceBoards.skillsDeveloped",
                                  )}
                                </h4>
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 bg-white/30 dark:bg-gray-800/30 p-3 rounded-lg">
                                <div className="prose prose-sm max-w-none dark:prose-invert">
                                  <EditableMarkdown
                                    value={activity.skillsDeveloped}
                                    isEditable={isEditable ?? true}
                                    handleSave={async (newValue) => {
                                      await updateActivity(
                                        index,
                                        "skillsDeveloped",
                                        newValue,
                                      );
                                    }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ChoiceBoardCardPDF
            customizationSettings={customizationSettings}
            choiceBoard={{ ...choiceBoard, imageUrl: finalImageUrl }}
          />
        }
      />
    </div>
  );
};

export default ChoiceBoardCard;
